// Analytics Manager for UsedFindr

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('analytics') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};
// Handles client-side analytics tracking and data aggregation

class AnalyticsManager {
  constructor() {
    this.storageKey = 'usedfindr_analytics';
    this.sessionKey = 'usedfindr_session';
    this.queueKey = 'usedfindr_analytics_event_queue';
    this.flushBatchSize = 25;
    this.flushDelayMs = 3000;
    this.isFlushing = false;
    this.flushTimer = null;
    this.initSession();
    this.initFlushHooks();
  }
  
  // Initialize or retrieve session data
  initSession() {
    let session = this.getSession();
    if (!session) {
      session = {
        id: this.generateSessionId(),
        startTime: Date.now(),
        pageViews: 0,
        clicks: 0,
        views: 0
      };
      this.saveSession(session);
    }
    return session;
  }
  
  // Generate unique session ID
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  // Get current session
  getSession() {
    try {
      const sessionData = sessionStorage.getItem(this.sessionKey);
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      logger.error('Error getting session:', error);
      return null;
    }
  }
  
  // Save session data
  saveSession(session) {
    try {
      sessionStorage.setItem(this.sessionKey, JSON.stringify(session));
    } catch (error) {
      logger.error('Error saving session:', error);
    }
  }
  
  // Get analytics data from localStorage
  getAnalytics() {
    try {
      const analyticsData = localStorage.getItem(this.storageKey);
      return analyticsData ? JSON.parse(analyticsData) : this.getDefaultAnalytics();
    } catch (error) {
      logger.error('Error getting analytics:', error);
      return this.getDefaultAnalytics();
    }
  }
  
  // Get default analytics structure
  getDefaultAnalytics() {
    return {
      clicks: {},
      views: {},
      pageViews: [],
      sessions: [],
      popularProducts: {},
      dailyStats: {}
    };
  }
  
  // Save analytics data to localStorage
  saveAnalytics(analytics) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(analytics));
    } catch (error) {
      logger.error('Error saving analytics:', error);
    }
  }

  // ---- Backend event queue (dual-write migration) ----
  initFlushHooks() {
    if (typeof window === 'undefined') return;

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushEventQueue({ useBeacon: true });
      }
    });
    window.addEventListener('beforeunload', () => {
      this.flushEventQueue({ useBeacon: true });
    });
  }

  getEventQueue() {
    try {
      const raw = localStorage.getItem(this.queueKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      logger.error('Error reading analytics event queue:', error);
      return [];
    }
  }

  saveEventQueue(queue) {
    try {
      localStorage.setItem(this.queueKey, JSON.stringify(queue));
    } catch (error) {
      logger.error('Error saving analytics event queue:', error);
    }
  }

  enqueueBackendEvent(event) {
    if (!window.consentManager?.hasConsent('analytics')) {
      return;
    }

    const queue = this.getEventQueue();
    queue.push(event);
    // Cap queue size to prevent unbounded growth in offline scenarios.
    if (queue.length > 500) {
      queue.splice(0, queue.length - 500);
    }
    this.saveEventQueue(queue);
    this.scheduleFlush();
  }

  scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushEventQueue();
    }, this.flushDelayMs);
  }

  getIngestUrl() {
    if (window.apiService?.baseUrl) {
      return `${window.apiService.baseUrl}/products/engagement-analytics/ingest/`;
    }
    return '/api/products/engagement-analytics/ingest/';
  }

  async sendBatch(events, { useBeacon = false } = {}) {
    if (!events.length) return true;

    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const payload = JSON.stringify({ events });
        const blob = new Blob([payload], { type: 'application/json' });
        return navigator.sendBeacon(this.getIngestUrl(), blob);
      } catch (error) {
        logger.warn('sendBeacon analytics flush failed:', error);
      }
    }

    if (window.apiService && typeof window.apiService.trackEngagementEvents === 'function') {
      await window.apiService.trackEngagementEvents(events);
      return true;
    }

    const response = await fetch(this.getIngestUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
      credentials: 'same-origin',
      keepalive: useBeacon,
    });
    return response.ok;
  }

  async flushEventQueue(options = {}) {
    if (this.isFlushing && !options.useBeacon) return;
    if (!window.consentManager?.hasConsent('analytics')) return;

    const queue = this.getEventQueue();
    if (!queue.length) return;

    if (options.useBeacon) {
      const batch = queue.slice(0, this.flushBatchSize);
      const ok = await this.sendBatch(batch, { useBeacon: true });
      if (ok) {
        this.saveEventQueue(queue.slice(batch.length));
      }
      return;
    }

    this.isFlushing = true;
    try {
      let current = this.getEventQueue();
      while (current.length) {
        const batch = current.slice(0, this.flushBatchSize);
        const ok = await this.sendBatch(batch);
        if (!ok) break;
        current = current.slice(batch.length);
        this.saveEventQueue(current);
      }
    } catch (error) {
      logger.warn('Analytics queue flush failed:', error);
    } finally {
      this.isFlushing = false;
    }
  }
  
  // Track product click
  trackClick(productId, context = {}) {
    // Check consent before tracking (GDPR/CCPA compliance)
    if (!window.consentManager?.hasConsent('analytics')) {
      return;
    }
    
    if (!productId) return;
    
    const analytics = this.getAnalytics();
    const timestamp = Date.now();
    const date = this.getDateKey(timestamp);
    
    // Track click count
    if (!analytics.clicks[productId]) {
      analytics.clicks[productId] = 0;
    }
    analytics.clicks[productId]++;
    
    // Track popular products
    if (!analytics.popularProducts[productId]) {
      analytics.popularProducts[productId] = {
        clicks: 0,
        views: 0,
        lastClick: null,
        lastView: null
      };
    }
    analytics.popularProducts[productId].clicks++;
    analytics.popularProducts[productId].lastClick = timestamp;
    
    // Track daily stats
    if (!analytics.dailyStats[date]) {
      analytics.dailyStats[date] = {
        clicks: 0,
        views: 0,
        pageViews: 0,
        sessions: 0
      };
    }
    analytics.dailyStats[date].clicks++;
    
    // Update session
    const session = this.getSession();
    if (session) {
      session.clicks++;
      this.saveSession(session);
    }
    
    this.saveAnalytics(analytics);
    const meta = { source: 'analytics_manager' };
    if (context.marketplace) meta.marketplace = String(context.marketplace);
    if (context.condition) meta.condition = String(context.condition).slice(0, 120);
    if (context.destinationUrl) meta.destination_url = String(context.destinationUrl).slice(0, 512);
    this.enqueueBackendEvent({
      event_type: 'click',
      product_id: String(productId || ''),
      category: String(context.category || ''),
      page_path: String(context.pagePath || window.location.pathname || ''),
      page_title: String(context.pageTitle || document.title || ''),
      session_id: String(session?.id || ''),
      marketplace: String(context.marketplace || ''),
      occurred_at: new Date(timestamp).toISOString(),
      metadata: meta,
    });
    this.flushEventQueue();
  }
  
  // Track product view
  trackView(productId, context = {}) {
    // Check consent before tracking (GDPR/CCPA compliance)
    if (!window.consentManager?.hasConsent('analytics')) {
      return;
    }
    
    if (!productId) return;
    
    const analytics = this.getAnalytics();
    const timestamp = Date.now();
    const date = this.getDateKey(timestamp);
    
    // Track view count
    if (!analytics.views[productId]) {
      analytics.views[productId] = 0;
    }
    analytics.views[productId]++;
    
    // Track popular products
    if (!analytics.popularProducts[productId]) {
      analytics.popularProducts[productId] = {
        clicks: 0,
        views: 0,
        lastClick: null,
        lastView: null
      };
    }
    analytics.popularProducts[productId].views++;
    analytics.popularProducts[productId].lastView = timestamp;
    
    // Track daily stats
    if (!analytics.dailyStats[date]) {
      analytics.dailyStats[date] = {
        clicks: 0,
        views: 0,
        pageViews: 0,
        sessions: 0
      };
    }
    analytics.dailyStats[date].views++;
    
    // Update session
    const session = this.getSession();
    if (session) {
      session.views++;
      this.saveSession(session);
    }
    
    this.saveAnalytics(analytics);
    this.enqueueBackendEvent({
      event_type: 'view',
      product_id: String(productId || ''),
      category: String(context.category || ''),
      page_path: String(context.pagePath || window.location.pathname || ''),
      page_title: String(context.pageTitle || document.title || ''),
      session_id: String(session?.id || ''),
      occurred_at: new Date(timestamp).toISOString(),
      metadata: { source: 'analytics_manager' },
    });
    this.flushEventQueue();
  }
  
  // Track page view
  trackPageView(pagePath, pageTitle) {
    // Check consent before tracking (GDPR/CCPA compliance)
    if (!window.consentManager?.hasConsent('analytics')) {
      return;
    }
    
    const analytics = this.getAnalytics();
    const timestamp = Date.now();
    const date = this.getDateKey(timestamp);
    
    // Add page view record
    analytics.pageViews.push({
      path: pagePath,
      title: pageTitle,
      timestamp: timestamp,
      date: date
    });
    
    // Keep only last 1000 page views to prevent localStorage bloat
    if (analytics.pageViews.length > 1000) {
      analytics.pageViews = analytics.pageViews.slice(-1000);
    }
    
    // Track daily stats
    if (!analytics.dailyStats[date]) {
      analytics.dailyStats[date] = {
        clicks: 0,
        views: 0,
        pageViews: 0,
        sessions: 0
      };
    }
    analytics.dailyStats[date].pageViews++;
    
    // Update session
    const session = this.getSession();
    if (session) {
      session.pageViews++;
      this.saveSession(session);
    }
    
    this.saveAnalytics(analytics);
    this.enqueueBackendEvent({
      event_type: 'page_view',
      product_id: '',
      category: '',
      page_path: String(pagePath || ''),
      page_title: String(pageTitle || ''),
      session_id: String(session?.id || ''),
      occurred_at: new Date(timestamp).toISOString(),
      metadata: { source: 'analytics_manager' },
    });
    this.flushEventQueue();
  }
  
  // Get date key (YYYY-MM-DD)
  getDateKey(timestamp) {
    const date = new Date(timestamp);
    return date.toISOString().split('T')[0];
  }
  
  // Get analytics summary
  getSummary() {
    const analytics = this.getAnalytics();
    const session = this.getSession();
    
    const totalClicks = Object.values(analytics.clicks).reduce((sum, count) => sum + count, 0);
    const totalViews = Object.values(analytics.views).reduce((sum, count) => sum + count, 0);
    const totalPageViews = analytics.pageViews.length;
    
    // Get top products (sorted by clicks first, then total engagement)
    const topProducts = Object.entries(analytics.popularProducts)
      .map(([productId, stats]) => ({
        productId,
        clicks: stats.clicks || 0,
        views: stats.views || 0,
        lastClick: stats.lastClick,
        lastView: stats.lastView
      }))
      .sort((a, b) => {
        // Sort by clicks first, then by total engagement (clicks + views)
        if (b.clicks !== a.clicks) {
          return b.clicks - a.clicks;
        }
        return (b.clicks + b.views) - (a.clicks + a.views);
      })
      .slice(0, 10);
    
    // Get daily stats for last 30 days
    const last30Days = this.getLastNDays(30);
    const dailyStats = last30Days.map(date => ({
      date,
      ...(analytics.dailyStats[date] || { clicks: 0, views: 0, pageViews: 0, sessions: 0 })
    }));
    
    return {
      totalClicks,
      totalViews,
      totalPageViews,
      topProducts,
      dailyStats,
      currentSession: session ? {
        id: session.id,
        duration: Date.now() - session.startTime,
        pageViews: session.pageViews,
        clicks: session.clicks,
        views: session.views
      } : null
    };
  }
  
  // Get last N days dates
  getLastNDays(n) {
    const dates = [];
    for (let i = n - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(this.getDateKey(date.getTime()));
    }
    return dates;
  }
  
  // Get weekly stats
  getWeeklyStats() {
    const analytics = this.getAnalytics();
    const last7Days = this.getLastNDays(7);
    
    return last7Days.map(date => ({
      date,
      ...(analytics.dailyStats[date] || { clicks: 0, views: 0, pageViews: 0, sessions: 0 })
    }));
  }
  
  // Get monthly stats
  getMonthlyStats() {
    const analytics = this.getAnalytics();
    const last30Days = this.getLastNDays(30);
    
    return last30Days.map(date => ({
      date,
      ...(analytics.dailyStats[date] || { clicks: 0, views: 0, pageViews: 0, sessions: 0 })
    }));
  }
  
  // Export analytics data as JSON (requires can_export_data permission)
  exportData() {
    if (!(typeof window !== 'undefined' && window.permissionService?.hasPermission('can_export_data'))) {
      return;
    }
    const analytics = this.getAnalytics();
    const session = this.getSession();
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      analytics,
      currentSession: session
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usedfindr-analytics-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  
  // Clear analytics data (user-initiated via UI)
  clearData() {
    if (confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
      localStorage.removeItem(this.storageKey);
      sessionStorage.removeItem(this.sessionKey);
      return true;
    }
    return false;
  }
  
  // Clear all analytics data (called automatically when consent is revoked)
  clearAllData() {
    try {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.queueKey);
      sessionStorage.removeItem(this.sessionKey);
      if (this.flushTimer) {
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
      }
      logger.info('Analytics data cleared due to consent withdrawal');
    } catch (error) {
      logger.error('Error clearing analytics data:', error);
    }
  }
}

// Create global instance
const analyticsManager = new AnalyticsManager();

// Expose for admin console (admin-analytics-inline.js runs before script.js and needs to create or use manager)
if (typeof window !== 'undefined') {
  window.AnalyticsManager = AnalyticsManager;
}

// Export for module usage
export { AnalyticsManager, analyticsManager };

